'use client';

import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAuthActions } from '@convex-dev/auth/react';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';

// ============================================================================
// ACCESSO NELL'INSTALLAZIONE DEL CLIENTE.
//
// Email e password, e basta. Niente posta di mezzo: un magazzino che tiene il
// gestionale in sede spesso non ha un server di posta, e legare il primo
// accesso a una mail che non parte vuol dire restare chiusi fuori di casa
// propria. Gli account li crea l'amministratore dalla pagina Squadra.
//
// Nel cloud questa schermata non esiste: li' c'e' quella di Clerk.
// ============================================================================

const INPUT =
  'w-full rounded-[10px] border border-stone-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-[11px] text-[15px] outline-none transition-colors focus:border-[var(--accent)]';

export function LocalSignIn() {
  const t = useTranslations('Auth.local');
  const { signIn } = useAuthActions();
  const params = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const raw = params.get('redirect') || '/app/dashboard';
  const redirectTo = raw.startsWith('/') ? raw : '/app/dashboard';

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);

    const form = new FormData(event.currentTarget);
    form.set('flow', 'signIn');
    form.set('email', String(form.get('email') ?? '').trim().toLowerCase());

    try {
      await signIn('password', form);
      window.location.href = redirectTo;
    } catch {
      // Un solo messaggio per email sbagliata e password sbagliata: dire quale
      // delle due e' errata racconta a un estraneo chi ha un account qui.
      setError(t('wrong'));
      setBusy(false);
    }
  }

  return (
    <div className="w-full max-w-[380px]">
      <h1 className="text-[26px] font-bold tracking-tight m-0 mb-1.5">{t('title')}</h1>
      <p className="text-[14.5px] text-stone-500 dark:text-zinc-400 m-0 mb-7">{t('body')}</p>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-stone-600 dark:text-zinc-300">{t('email')}</span>
          <input name="email" type="email" required autoComplete="username" className={INPUT} />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-stone-600 dark:text-zinc-300">{t('password')}</span>
          <input name="password" type="password" required autoComplete="current-password" className={INPUT} />
        </label>

        {error && <p className="text-[13.5px] text-red-600 dark:text-red-400 m-0">{error}</p>}

        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 px-6 py-[13px] text-[15px] font-semibold rounded-[10px] bg-stone-950 text-stone-50 dark:bg-zinc-50 dark:text-zinc-950 hover:opacity-90 transition-opacity disabled:opacity-60 mt-1"
        >
          {busy && <Loader2 size={15} className="animate-spin" />}
          {busy ? t('entering') : t('enter')}
        </button>
      </form>

      <p className="text-[13px] text-stone-400 dark:text-zinc-500 mt-6 m-0 leading-[1.55]">{t('forgot')}</p>
    </div>
  );
}
